/* eslint-disable no-console */
import { push } from 'react-router-redux';
import { delay } from 'redux-saga';
import { call, put, takeLatest, select, takeEvery } from 'redux-saga/effects';
import { uniq } from 'lodash';
import {
    searchElementMatchWithoutLanguage,
    setNotification,
    locales,
    getTranslated,
    searchElementMatch,
    searchByOrOperator,
} from 'utils';
import * as ProductGroupActions from '../actions/productGroupActions';
import * as ProductGroupApi from '../api/productGroupApi';
import { getPackagesApi } from '../api/packageApi';
import { getUsersApi } from '../api/userApi';
import { getPropertiesApi } from '../api/propertyApi';
import { getConfigurationsApi } from '../api/configurationApi';
import * as ProductGroupTypes from '../types/productGroupTypes';

const getLayout = state => state.layout;
const getForm = state => state.form;
const getProductGroups = state => state.productGroup;

// L O A D   P R O D U C T   G R O U P S
function* loadProductGroupsSaga() {
    try {
        const productGroup = yield select(getProductGroups);

        const { offset, limit, sort } = productGroup;

        const params = {
            offset,
            limit,
        };

        if (sort !== null) {
            if (sort.key === 'name') {
                params.sort_by = JSON.stringify({ unique_name: sort.type === 'asc' ? 1 : -1 });
            } else if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sort.type === 'asc' ? 1 : -1 });
            }
        }

        if (productGroup.filters.name !== '') {
            params.filter = JSON.stringify({
                name: searchElementMatchWithoutLanguage(productGroup.filters.name),
            });
        }

        const result = yield ProductGroupApi.getProductGroupsApi(params);
        yield put(ProductGroupActions.loadProductGroupsRes(result.data));
    } catch (e) {
        console.log(e);
        yield put(ProductGroupActions.loadProductGroupsErr());
    }
}

export function* loadProductGroupsWatcher() {
    yield takeLatest(ProductGroupTypes.LOAD_PRODUCT_GROUPS, loadProductGroupsSaga);
}
// L O A D   P R O D U C T   G R O U P S


// T O G G L E   P R O D U C T   G R O U P S   S T A T U S
function* toggleProductGroupsStatusSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        let params = {};

        if (action.multiple) {
            params = action.bulkParams;
        } else {
            params = {
                product_group_id: action.id[0],
                status: action.status,
            };
        }

        const result = yield ProductGroupApi.toggleProductGroupStatusApi(params);

        if (result.status === 202) {
            if (!action.multiple) {
                setNotification('success', locale, 'global.success', `productGroup.notification.toggleStatus.${action.status}.success`);
                yield put(ProductGroupActions.toggleProductGroupsStatusRes());
            } else {
                setNotification('success', locale, 'global.success', `productGroup.notification.bulkStatus.${action.status}.success`);
                yield put(ProductGroupActions.bulkCheckedProductGroupsRes());
            }

            yield call(loadProductGroupsSaga, {});
        } else if (!action.multiple) {
            setNotification('error', locale, 'global.error', `productGroup.notification.toggleStatus.${action.status}.error`);
            yield put(ProductGroupActions.toggleProductGroupsStatusErr());
        } else {
            setNotification('error', locale, 'global.error', `productGroup.notification.bulkStatus.${action.status}.error`);
            yield put(ProductGroupActions.bulkCheckedProductGroupsErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', `productGroup.notification.toggleStatus.${action.status}.error`);
        yield put(ProductGroupActions.toggleProductGroupsStatusErr());
        console.log(e);
    }
}

export function* toggleProductGroupsStatusWatcher() {
    yield takeLatest(ProductGroupTypes.TOGGLE_PRODUCT_GROUPS_STATUS, toggleProductGroupsStatusSaga);
}
// T O G G L E   P R O D U C T   G R O U P S   S T A T U S


// A D D   N E W   P R O D U C T   G R O U P S   W I T H   T R A N S L A T E   &&  C A L C U L A T E   F O R M U L A
function* addCalculFormula(action) {
    const layout = yield select(getLayout);
    const form = yield select(getForm);

    const { locale } = layout;

    const data = {
        product_group_id: action.data.product_group_id,
        reseller_id: action.data.reseller_id,
        formula_type: 'AllPrice',
        formula: action.data.calculFormula,
    };

    try {
        const dataTrans = {
            ...data,
        };

        const { values } = form.productGroupForm;

        if (values.calculFormula.length > 0) {
            const resultTrans = yield ProductGroupApi.addCalculFormulaApi(dataTrans);
            if (resultTrans.status !== 202) {
                const error = yield resultTrans.json();
                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            }
        }
    } catch (e) {
        yield put(ProductGroupActions.addNewProductGroupsErr());
    }
}

function* updateProductGroupTranslate(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    let finalDescription = 'productGroup.notification.newProductsGroupsAddedSuccess';
    let type = 'add';

    const data = {
        product_group_id: action.data.product_group_id,
        reseller_id: action.data.reseller_id,
        description: '',
        content: '',
    };

    if (action.type === ProductGroupTypes.EDIT_PRODUCT_GROUPS) {
        type = 'edit';
        finalDescription = 'productGroup.notification.productsGroupsEditedSuccess';
    }

    for (let i = 0; i < locales.length; i += 1) {
        const lc = locales[i];

        if (lc !== 'en' || (lc === 'en' && action.type === ProductGroupTypes.EDIT_PRODUCT_GROUPS)) {
            try {
                const dataTrans = {
                    ...data,
                    ...action.data[lc],
                };

                const resultTrans = yield ProductGroupApi.updateProductGroupTranslateApi(dataTrans);

                if (resultTrans.status !== 202) {
                    const error = yield resultTrans.json();
                    setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                }
            } catch (e) {
                setNotification('error', locale, 'global.error', 'property.notification.translateError', { language: getTranslated(`locale.${lc}`, locale) });
            }
        }
    }

    yield put(ProductGroupActions.updateTranslateProductGroups(true));

    yield put(ProductGroupActions.updateTranslateProductGroupsRes(type, false, true));

    if (action.type === ProductGroupTypes.EDIT_PRODUCT_GROUPS) {
        yield call(addCalculFormula, action);
        yield put(push('/marketing/products_groups/'));
    }

    yield call(addCalculFormula, action);

    yield delay(500);
    yield put(push('/marketing/products_groups/'));
    yield put(ProductGroupActions.updateTranslateProductGroupsRes(type, false, false));

    setNotification('success', locale, 'global.success', finalDescription);
}

function* addNewProductGroupsSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const data = {
            product_group_id: action.data.product_group_id,
            reseller_id: action.data.reseller_id,
            ...action.data.en,
            tva_type: action.data.tva_type,
            tags: action.data.tags,
            description: action.data.en.description || '',
            content: action.data.en.content || '',
            properties_order: action.data.properties_order,
        };

        const result = yield ProductGroupApi.addNewProductGroupApi(data);
        if (result.status === 202) {
            yield put(ProductGroupActions.addNewProductGroupsRes(true));
            yield call(updateProductGroupTranslate, action);

            yield put(ProductGroupActions.addNewProductGroupsRes(false));

            yield put(ProductGroupActions.goToStepOfProductGroupsForm(1, action.data.product_group_id));
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });

            yield put(ProductGroupActions.addNewProductGroupsErr(error.message));
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'productGroup.notification.addingNewProductsGroupsError');
        yield put(ProductGroupActions.addNewProductGroupsErr());
    }
}

export function* addNewProductGroupsWatcher() {
    yield takeLatest(ProductGroupTypes.ADD_NEW_PRODUCT_GROUPS, addNewProductGroupsSaga);
}
// A D D   N E W   P R O D U C T   G R O U P S   W I T H   T R A N S L A T E   &&   C A L C U L A T E   F O R M U L A


// L O A D   E D I T A B L E   P A C K A G E S   F R O M   P R O D U C T   G R O U P S
function* loadEdiTablePackagesFromProductGroupSaga(action) {
    const productGroup = yield select(getProductGroups);
    const { edit } = productGroup;
    const { offset, limit, filters, sort } = productGroup.chosen_packages;

    try {
        if (action.ids !== undefined && action.ids.length) {
            const params = {
                offset,
                limit,
            };

            const sendFilters = {
                product_group_ids: edit.data.id,
            };

            if (filters.name !== '') {
                sendFilters.name = {
                    operator: 'typing',
                    value: filters.name,
                };
            }

            if (filters.created_by.length > 0) {
                sendFilters.created_by = filters.created_by;
            }

            params.filter = JSON.stringify(sendFilters);

            if (sort !== null) {
                const sortInt = sort.type === 'asc' ? 1 : -1;

                if (sort.key === 'name') {
                    params.sort_by = JSON.stringify({ name: sortInt });
                } else if (sort.key === 'created_at') {
                    params.sort_by = JSON.stringify({ created_at: sortInt });
                }
            }

            const resultPackage = yield getPackagesApi(params);

            const paramUsers = {
                filter: JSON.stringify({ _id: uniq(resultPackage.data.hits.map(h => h.created_by)) }),
            };

            const getUsers = yield getUsersApi(paramUsers);
            yield put(ProductGroupActions.loadDisplayUsersForAttachedPackages(getUsers.data.hits));

            yield put(ProductGroupActions.loadEdiTablePackagesFromProductGroupsRes(resultPackage.data));
        } else {
            yield put(ProductGroupActions.loadEdiTablePackagesFromProductGroupsRes(null));
        }
    } catch (e) {
        yield put(ProductGroupActions.loadEdiTablePackagesFromProductGroupsErr());
        console.log(e);
    }
}

export function* loadEdiTablePackagesFromProductGroupWatcher() {
    yield takeLatest(ProductGroupTypes.LOAD_EDITTABLE_PACKAGES_FROM_PRODUCT_GROUPS, loadEdiTablePackagesFromProductGroupSaga);
}
// L O A D   E D I T A B L E   P A C K A G E S   F R O M   P R O D U C T   G R O U P S


// L O A D   E D I T A B L E   P R O D U C T   G R O U P S
function* loadEditableProductGroupsSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const paramsProperty = {
            filter: JSON.stringify({ id: action.id }),
        };

        const result = yield ProductGroupApi.getProductGroupsApi(paramsProperty);

        if (result.data.hits.length > 0) {
            const hit = result.data.hits[0];
            let properties = [];

            if (action.properties) {
                const resutlProperties = yield getPropertiesApi({
                    filter: JSON.stringify({ product_group: action.id }),
                });

                if (resutlProperties.data.hits.length > 0) {
                    properties = resutlProperties.data.hits;
                }
            }

            yield put(ProductGroupActions.loadEditableProductGroupsRes({
                ...hit,
                properties,
            }));

            if (action.packages) {
                yield put(ProductGroupActions.loadEdiTablePackagesFromProductGroups());
                yield call(loadEdiTablePackagesFromProductGroupSaga, { ids: hit.packages });
            }
        } else {
            yield put(push('/404'));
            yield put(ProductGroupActions.loadEditableProductGroupsRes(null));
        }
    } catch (e) {
        yield put(ProductGroupActions.loadEditableProductGroupsErr());
        setNotification('error', locale, 'global.error', 'productGroup.notification.loadProductsGroupsError');
    }
}

export function* loadEditableProductGroupsWatcher() {
    yield takeLatest(ProductGroupTypes.LOAD_EDITABLE_PRODUCT_GROUPS, loadEditableProductGroupsSaga);
}
// L O A D   E D I T A B L E   P R O D U C T   G R O U P S


// E D I T   P R O D U C T   G R O U P S
function* editProductGroupsSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const data = {
            product_group_id: action.data.product_group_id,
            reseller_id: action.data.reseller_id,
            ...action.data.en,
            tva_type: action.data.tva_type,
            tags: action.data.tags,
            description: action.data.en.description || '',
            content: action.data.en.content || '',
            properties_order: action.data.properties_order,
        };
        const result = yield ProductGroupApi.editProductGroupApi(data);

        if (result.status === 202) {
            yield call(updateProductGroupTranslate, action);
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(ProductGroupActions.editProductGroupsErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'productGroup.notification.loadProductsGroupsError');
        yield put(ProductGroupActions.editProductGroupsErr());
    }
}

export function* editProductGroupsWatcher() {
    yield takeLatest(ProductGroupTypes.EDIT_PRODUCT_GROUPS, editProductGroupsSaga);
}
// E D I T   P R O D U C T   G R O U P S


// L O A D   S E L L   P R I C E
function* loadProductGroupForSellPricesSaga(action) {
    try {
        const params = {
            filter: JSON.stringify({ id: action.id }),
        };

        const result = yield ProductGroupApi.getProductGroupsApi(params);

        if (result.status === 200) {
            const { hits } = result.data;
            yield put(ProductGroupActions.loadProductGroupForSellPriceRes(hits[0]));
        }
    } catch (e) {
        yield put(ProductGroupActions.loadProductGroupForSellPriceErr());
        console.log(e);
    }
}

function* loadSellPricesSaga(action) {
    const { locale } = yield select(getLayout);
    const { sellPrice } = yield select(getProductGroups);
    const { productGroup, offset, limit, filters, sort } = sellPrice;

    try {
        const sendFilters = {
            all: true,
            product_groups: [action.payload],
        };

        const params = {
            filter: JSON.stringify(sendFilters),
            offset,
            limit,
        };

        if (filters['name.translate'] !== '') {
            sendFilters['name.translate'] = searchElementMatch(filters['name.translate'], locale);
        }

        if (filters['configuration_property.carrier_id'].length > 0) {
            sendFilters['configuration_property.carrier_id'] = filters['configuration_property.carrier_id'];
        }

        if (filters['configuration_property.property_value_id'].length > 0) {
            sendFilters.and = filters['configuration_property.property_value_id'].map(cp => ({
                'configuration_property.property_value_id': cp,
            }));
        }

        if (filters.configuration_type.length > 0) {
            sendFilters.configuration_type = filters.configuration_type;
        }

        if (filters['cost_durations.partner_id'].length > 0) {
            sendFilters['cost_durations.partner_id'] = filters['cost_durations.partner_id'];
        }

        params.filter = JSON.stringify(sendFilters);

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;

            if (sort.key === 'name') {
                params.sort_by = JSON.stringify({ 'name.original_name': sortInt });
            } else if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            }
        }

        if (productGroup.data === null) {
            yield call(loadProductGroupForSellPricesSaga, { id: action.payload });
            yield delay(250);
        }

        const result = yield getConfigurationsApi(params);

        if (result.status === 200) {
            yield put(ProductGroupActions.loadSellPricesRes(result.data));
        }
    } catch (e) {
        yield put(ProductGroupActions.loadSellPricesErr());
        console.log(e);
    }
}

export function* loadSellPricesWatcher() {
    yield takeLatest(ProductGroupTypes.LOAD_SELL_PRICE, loadSellPricesSaga);
}
// L O A D   S E L L   P R I C E


// U P D A T E   S E L L   P R I C E
function* updateSellPriceSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const bulkParams = { bulk: action.data };
        const result = yield ProductGroupApi.updateSellPriceApi(bulkParams);

        if (result.status === 202) {
            setNotification('success', locale, 'global.success', 'productGroup.sellPrice.notification.success');

            const costDurationIds = action.data.map(s => s.cost_and_duration_id);

            yield put(ProductGroupActions.updateSellPriceRes(costDurationIds));
            yield delay(costDurationIds.length * 100);
            yield put(ProductGroupActions.updateSellPriceReq(costDurationIds));

            yield put({ type: ProductGroupTypes.LOAD_SELL_PRICE, payload: action.data[0].product_group_id });
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });

            yield put(ProductGroupActions.updateSellPriceErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'productGroup.sellPrice.notification.error');
        yield put(ProductGroupActions.updateSellPriceErr());
        console.log(e);
    }
}

export function* updateSellPriceWatcher() {
    yield takeEvery(ProductGroupTypes.UPDATE_SELL_PRICE, updateSellPriceSaga);
}
// U P D A T E   S E L L   P R I C E


// T O G G L E   C O S T   S T A T U S   B Y   S E L L   P R I C E
function* toggleCostStatusBySellPriceSaga(action) {
    const { locale } = yield select(getLayout);
    const { sellPrice } = yield select(getProductGroups);
    const { ids, status } = action;

    try {
        const params = {
            bulk: ids.map(id => ({
                sell_price_id: id,
                status: status === 'enable',
            })),
        };

        const result = yield ProductGroupApi.toggleSellPriceStatus(params);

        if (result.status === 202) {
            setNotification('success', locale, 'global.success', `productGroup.sellPrice.notification.toggleStatus.${status}.success`);
            yield put(ProductGroupActions.toggleCostStatusBySellPriceRes());

            yield call(loadSellPricesSaga, { payload: sellPrice.productGroup.data.id });
        } else {
            yield put(ProductGroupActions.toggleCostStatusBySellPriceErr());
            setNotification('error', locale, 'global.error', `productGroup.sellPrice.notification.toggleStatus.${status}.error`);
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', `productGroup.sellPrice.notification.toggleStatus.${status}.error`);
        yield put(ProductGroupActions.toggleCostStatusBySellPriceErr());
        console.log(e);
    }
}
export function* toggleCostStatusBySellPriceWatcher() {
    yield takeLatest(ProductGroupTypes.TOGGLE_COST_STATUS_BY_SELL_PRICE, toggleCostStatusBySellPriceSaga);
}
// T O G G L E   C O S T   S T A T U S   B Y   S E L L   P R I C E


// L O A D   P A C K A G E S   F O R   P R O D U C T   G R O U P S
function* loadPackagesForProductGroupsSaga() {
    const productGroup = yield select(getProductGroups);
    const { offset, limit, filters, sort } = productGroup.packages;

    try {
        const params = {
            offset,
            limit,
        };

        const sendFilters = {};

        if (filters.name !== '') {
            sendFilters.name = {
                operator: 'typing',
                value: filters.name,
            };
        }

        if (filters.created_by.length > 0) {
            sendFilters.created_by = filters.created_by;
        }

        params.filter = JSON.stringify(sendFilters);

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;

            if (sort.key === 'name') {
                params.sort_by = JSON.stringify({ name: sortInt });
            } else if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            }
        }

        const result = yield getPackagesApi(params);
        yield put(ProductGroupActions.loadPackagesForProductGroupsRes(result.data));
    } catch (e) {
        yield put(ProductGroupActions.loadPackagesForProductGroupsErr());
        console.log(e);
    }
}

export function* loadPackagesForProductGroupsWatcher() {
    yield takeEvery(ProductGroupTypes.LOAD_PACKAGES_FOR_PRODUCT_GROUPS, loadPackagesForProductGroupsSaga);
}
// L O A D   P A C K A G E S   F O R   P R O D U C T   G R O U P S


// L O A D   U S E R S   F O R   P A C K A G E
function* loadUsersForPackageSaga(action) {
    try {
        const params = {
            filter: JSON.stringify({
                or: searchByOrOperator(['first_name', 'last_name'], action.name),
            }),
        };

        const result = yield getUsersApi(params);

        if (result.status === 200) {
            yield put(ProductGroupActions.loadUsersForPackageRes(result.data.hits));
        } else {
            yield put(ProductGroupActions.loadUsersForPackageRes([]));
        }
    } catch (e) {
        console.log(e);
    }
}

export function* loadUsersForPackageToProductGroupsWatcher() {
    yield takeLatest(ProductGroupTypes.LOAD_USERS_FOR_PACKAGE, loadUsersForPackageSaga);
}
// L O A D   U S E R S   F O R   P A C K A G E


// A D D   B U L K   P A C K A G E S
function* addBulkPackagesSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const { pathname } = window.location;
        pathname.substring(pathname.lastIndexOf('/') + 1);

        for (let i = 0; i < action.data.length; i += 1) {
            const pack = action.data[i];
            // const params = {
            //     bulk: pack.map(c => ({
            //         package_id: c,
            //         product_group_id: pathname.substring(pathname.lastIndexOf('/') + 1),
            //     })),
            // };
            const params = {
                package_id: pack,
                product_group_id: pathname.substring(pathname.lastIndexOf('/') + 1),
            };

            yield ProductGroupApi.attachPackagesToProductGroupApi(params);
        }
        yield delay(action.data.length * 200);
        yield put(ProductGroupActions.addBulkPackagesForProductGroupsRes());
        yield put(push('/marketing/products_groups'));
        setNotification('success', locale, 'global.success', 'package.notification.addBulkPackageSuccess');
    } catch (e) {
        yield put(ProductGroupActions.addBulkPackagesForProductGroupsErr(getTranslated('package.notification.addBulkPackageError', locale)));
        setNotification('error', locale, 'global.error', 'package.notification.addBulkPackageError');
        console.log(e);
    }
}

export function* addBulkPackagesForProductGroupsWatcher() {
    yield takeLatest(ProductGroupTypes.ADD_BULK_PACKAGES_FOR_PRODUCT_GROUPS, addBulkPackagesSaga);
}
// A D D   B U L K   P A C K A G E S


// D E L E T E    B U L K   P A C K A G E S
function* deleteBulkPackagesSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const { pathname } = window.location;
        pathname.substring(pathname.lastIndexOf('/') + 1);

        for (let i = 0; i < action.data.length; i += 1) {
            const pack = action.data[i];
            // const params = {
            //     bulk: pack.map(c => ({
            //         package_id: c,
            //         product_group_id: pathname.substring(pathname.lastIndexOf('/') + 1),
            //     })),
            // };
            const params = {
                package_id: pack,
                product_group_id: pathname.substring(pathname.lastIndexOf('/') + 1),
            };
            yield ProductGroupApi.detachPackagesToProductGroupApi(params);
        }

        yield delay(action.data.length * 200);
        yield put(ProductGroupActions.deleteBulkPackagesForProductGroupsRes());
        yield put(push('/marketing/products_groups'));
        setNotification('success', locale, 'global.success', 'package.notification.deleteBulkPackageSuccess');
    } catch (e) {
        yield put(ProductGroupActions.deleteBulkPackagesForProductGroupsErr(getTranslated('package.notification.deleteBulkPackageError', locale)));
        setNotification('error', locale, 'global.error', 'package.notification.deleteBulkPackageError');
        console.log(e);
    }
}

export function* deleteBulkPackagesForProductGroupsWatcher() {
    yield takeLatest(ProductGroupTypes.DELETE_BULK_PACKAGES_FOR_PRODUCT_GROUPS, deleteBulkPackagesSaga);
}
// D E L E T E    B U L K   P A C K A G E S
